// /app/comments — Comments Inbox landing.
//
// Mirrors /app/content's pattern: list brands, auto-bounce to the
// single brand's inbox if there's only one, prompt to create a brand
// if there are zero. Comments are per-brand because each brand has its
// own platform accounts and reply tone.
//
// v0.2.x note: when the workspace-wide "all unread across all brands"
// query lands in comments-service (spec §11), this page becomes a
// cross-brand unified inbox instead of a picker.

import { useEffect, useState } from "react";
import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import { brands as brandsAPI, type Brand } from "../../../lib/api";

function CommentsHome() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [state, setState] = useState<
    { kind: "loading" } | { kind: "ready"; list: Brand[] } | { kind: "error"; msg: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    brandsAPI.list()
      .then((r) => !cancelled && setState({ kind: "ready", list: r.brands }))
      .catch((e) => !cancelled && setState({ kind: "error", msg: String(e) }));
    return () => { cancelled = true; };
  }, []);

  if (state.kind === "loading") {
    return <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12"><Card><p className="text-secondary">{t("common.loading")}</p></Card></div>;
  }
  if (state.kind === "error") {
    return <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12"><Card><p className="text-error">{state.msg}</p></Card></div>;
  }
  if (state.list.length === 1) {
    return <Navigate to="/app/brands/$brandId/comments" params={{ brandId: state.list[0]!.id }} replace />;
  }
  if (state.list.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card>
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <span className="eyebrow eyebrow-pill inline-flex">{t("inbox.title")}</span>
            <p className="text-secondary text-sm">{t("brands.empty.subtitle")}</p>
            <Button variant="primary" onClick={() => nav({ to: "/app/brands/new" })}>
              + {t("brands.new")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="eyebrow">{t("inbox.eyebrow")}</span>
        <h1 className="font-display text-3xl font-semibold">{t("inbox.title")}</h1>
        <p className="text-secondary text-sm">{t("inbox.pickBrand")}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {state.list.map((b) => (
          <Link
            key={b.id}
            to="/app/brands/$brandId/comments"
            params={{ brandId: b.id }}
            className="ring-nexus rounded-[14px]"
          >
            <Card className="!p-5 hover:bg-overlay/40 transition h-full">
              <span className="eyebrow">{b.country} · {b.primary_language}</span>
              <h2 className="font-display text-lg font-semibold mt-2 truncate" title={b.trade_name}>{b.trade_name}</h2>
              <p className="text-xs text-muted font-mono mt-1 truncate" title={b.slug}>/{b.slug}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/comments/")({
  component: CommentsHome,
});
